import { Flex, Loader } from '@fareye/ui'
import { FaCheckCircle, FaDotCircle, FaFlag, isEmpty } from '@fareye/utils'
import React from 'react'
import { useInView } from 'react-intersection-observer'
import { FieldValue } from './FieldValue'
import { displayTimeSecondFormat } from './function'
import { Stage } from './Stage'
import { EndStage, Span, style, TimeLine } from './styled'
import { TTansportModes } from './types'
import { IconWithPopover } from './IconWithPopover'
import { colorOption } from '../SummaryV2'
import {
  extraInfoForStop,
  getRunningStatusObj,
  standardizeText
} from '../../utility/funcs'
import { KPI_COLORS } from '@fareye/common'
import { statusColorOption } from '@fareye/configs'
import {
  DoubleDottedCircle,
  AtNodeAir,
  AtNodeRail,
  AtNodeShip,
  AtNodeTruck
} from '@fareye/utils'

interface IAtNodeMode {
  showVehicle?: Boolean
  modeType?: string
  atOrigin?: boolean
  stops?: any
}
function AtNodeMode(props: IAtNodeMode) {
  const { showVehicle, modeType, atOrigin, stops } = props
  const imgStyleForOrigin = {
    position: 'relative',
    top: isEmpty(stops) ? '6px' : '2px',
    paddingBottom: isEmpty(stops) ? '6px' : '4px',
    left: '2px',
    width: '30px',
    height: '30px',
    boxSizing: 'content-box'
  } as React.CSSProperties
  const imgStyleForNode = {
    position: 'relative',
    top: '-8px',
    left: '2px',
    width: '30px',
    height: '30px',
    marginBottom: '-8px',
    boxSizing: 'content-box'
  } as React.CSSProperties
  const imgStyle = atOrigin ? imgStyleForOrigin : imgStyleForNode
  if (showVehicle) {
    if (modeType == 'road') {
      return <img src={AtNodeTruck} style={imgStyle} />
    } else if (modeType == 'rail') {
      return <img src={AtNodeRail} style={imgStyle} />
    } else if (modeType == 'air') {
      return <img src={AtNodeAir} style={imgStyle} />
    } else if (modeType == 'ocean') {
      return <img src={AtNodeShip} style={imgStyle} />
    }
  } else if (atOrigin) {
    return (
      <img
        src={DoubleDottedCircle}
        style={{
          position: 'relative',
          top: isEmpty(stops) ? '4px' : '1px',
          paddingTop: '5px',
          paddingBottom: isEmpty(stops) ? '5px' : '4px',
          left: '1px',
          width: '24px',
          height: '24px',
          boxSizing: 'content-box'
        }}
      />
    )
  }
  return (
    <div
      style={{
        ...style.iconStyle,
        borderColor: '#1776D3',
        background: '#fff',
        backgroundColor: '#E5F6FF',
        border: '2px solid',
        height: '20px',
        width: '20px',
        position: 'relative',
        top: '-6px',
        marginBottom: '-14px',
        borderStyle: 'dotted',
        boxSizing: 'content-box'
      }}
    />
  )
}

export function ShipmentTimeLine({ rowData, timelineData }: any) {
  const originalData = rowData
  const untrackedFlag = originalData?.inState === 'untracked_state'
  const completedFlag = originalData?.inState === 'completed'
  const scheduledFlag = originalData?.inState === 'scheduled'

  const percCompleted = originalData.percTripCovered

  let transportMode: TTansportModes = originalData.transportMode
  const [ref, inView, entry] = useInView({
    threshold: 0,
    triggerOnce: true
  })
  const [loading, setLoading] = React.useState(() => !timelineData)
  let stopLen = originalData?.stops.length
  let stops = [...originalData?.stops.slice(1, stopLen - 1)]
  const originData = originalData?.stops[0] || {}
  const destinationData = originalData?.stops[stopLen - 1] || {}
  const nextStop = originalData?.nextStop || {}
  const nextStopIsOrigin = originData?.nodeId === nextStop?.nodeId && originData?.sequence === nextStop?.sequence
  const nextStopIsDestination = destinationData?.nodeId === nextStop?.nodeId && destinationData?.sequence === nextStop?.sequence
  const nextStopIndex = nextStopIsDestination
    ? -1
    : stops.findIndex((item: any) => item?.nodeId === nextStop?.nodeId && item?.sequence === nextStop?.sequence)
  const lastUpdatedTime = originalData?.lastLocation?.gpsTimestamp
    ? new Date(originalData.lastLocation.gpsTimestamp)
    : false
  const currentTime = new Date()
  const locationNotFound = originalData.locationNotFound
    ? originalData.locationNotFound
    : lastUpdatedTime
    ? (currentTime.getTime() - lastUpdatedTime.getTime()) / (60 * 60 * 1000) > 8
    : false
  const noLineToShow = nextStopIsOrigin || scheduledFlag
  // to test many stoppages simply spread same stops again n again
  let allowedOnTimeline = stops.length
  const completedOriginFlag = originData?.atd?.time
  const furtherStopsCompletedFlag = stops.find(
    (item: any) =>
      item.state === 'completed' || item?.ata?.time || item?.atd?.time
  )
    ? true
    : completedFlag || destinationData?.ata?.time
  if (!isEmpty(originalData.nextStop)) {
    allowedOnTimeline = allowedOnTimeline + 1
  }
  const timelineMinWidthPerStop = percCompleted / allowedOnTimeline
  let countVehicle = 0
  const statusColor: any = {
    early: statusColorOption.early,
    on_time: statusColorOption.on_time,
    delayed: statusColorOption.delayed
  }
  function whenToShowVehicle() {
    if (!countVehicle) {
      if (noLineToShow) {
        return false
      } else {
        countVehicle = 1
        return true
      }
    }
    return false
  }

  function Origin() {
    const origin = originalData?.startLocation?.locationName
    const std = displayTimeSecondFormat(originData?.std)
    const aetdFlag = originData?.atd?.time || completedFlag
    const sta = displayTimeSecondFormat(originData?.sta)
    const aetd = aetdFlag
      ? displayTimeSecondFormat(originData?.ata)
      : displayTimeSecondFormat(originData?.eta)
    const originDataStatus = standardizeText(originData?.status)
    let colorText = statusColor[originDataStatus]
    if (!colorText) {
      colorText = '#000000'
    }
    const completedStopFlag = stops.find(
      (item: any) => item.state === 'completed' || item?.ata?.time
    )
    const intialStopFlag = !isEmpty(stops)
      ? stops[0]?.ata?.time && !stops[0]?.atd?.time
      : false
    const showSolid =
      completedStopFlag ||
      completedFlag ||
      intialStopFlag ||
      nextStopIsDestination
    const showSolidForStops = completedFlag || destinationData?.ata?.time
    const showVehicleAtOrigin = isEmpty(stops)
      ? !showSolidForStops
      : showSolid || completedOriginFlag
      ? false
      : whenToShowVehicle()
    const atOriginFlag = !completedOriginFlag && !furtherStopsCompletedFlag
    const originIconStyle = {
      border: '4px solid',
      height: '17px',
      width: '17px',
      marginBottom: isEmpty(stops) ? '14px' : '17px',
      marginTop: isEmpty(stops) ? '-2px' : '-5px',
      position: 'relative',
      top: isEmpty(stops) ? '10px' : '11px',
      left: '4px',
      boxSizing: 'content-box'
    } as React.CSSProperties
    const originIconToDisplay = nextStopIsOrigin ? (
      <div
        style={{
          ...style.iconStyle,
          ...originIconStyle,
          borderColor: '#ADADAD',
          background: '#ADADAD',
          backgroundColor: '#fff'
        }}
      />
    ) : atOriginFlag ? (
      <AtNodeMode
        showVehicle={showVehicleAtOrigin}
        modeType={transportMode}
        atOrigin={true}
        stops={stops}
      />
    ) : (
      <div
        style={{
          ...style.iconStyle,
          ...originIconStyle,
          borderColor: '#1192E8',
          background: '#1192E8',
          backgroundColor: '#fff'
        }}
      />
    )
    return (
      <EndStage alignItemsFlexEnd>
        <Span
          variant="ellipsis"
          style={{
            width: '100%',
            textAlign: 'right',
            position: 'relative',
            top: '-2px',
            fontSize: '12px',
            boxSizing: 'content-box'
          }}
        >
          {origin || '-'}
        </Span>
        <IconWithPopover
          originData={originalData}
          popoverType="origin"
          transportMode={transportMode}
          icon={originIconToDisplay}
        />
        <Flex
          column
          alignItemsCenter
          style={{
            gap: '0.25em',
            marginTop: isEmpty(stops) ? '-4px' : '-10px',
            width: '100%',
            boxSizing: 'content-box'
          }}
        >
          <FieldValue
            style={{
              marginTop: 4,
              width: '160px',
              justifyContent: 'flex-start',
              position: 'relative',
              left: '15px',
              boxSizing: 'content-box'
            }}
            label={sta != '-' ? 'STA:' : '\u00a0'}
            valueStyle={{ fontSize: '12px' }}
            labelStyle={{ fontSize: '12px' }}
            value={sta != '-' ? sta : '\u00a0'}
          />
          <FieldValue
            style={{
              width: '160px',
              justifyContent: 'flex-start',
              position: 'relative',
              left: '15px',
              boxSizing: 'content-box'
            }}
            label={aetd != '-' ? (aetdFlag ? 'ATA:' : 'ETA:') : '\u00a0'}
            value={aetd != '-' ? aetd : '\u00a0'}
            labelStyle={{
              color: colorText,
              fontSize: '12px'
            }}
            valueStyle={{
              color: colorText,
              fontSize: '12px'
            }}
          />
        </Flex>
      </EndStage>
    )
  }

  function Destination() {
    const destination = originalData.endLocation?.locationName
    const sta = displayTimeSecondFormat(destinationData?.sta)
    const aetaFlag = destinationData?.ata?.time || completedFlag
    const colorCode =
      destinationData.nodeColor === 'green'
        ? '#279B4E'
        : destinationData.nodeColor === 'blue'
        ? '#1192E8'
        : destinationData.nodeColor === 'orange'
        ? '#EE6252'
        : '#BFBFBF'
    const aeta = aetaFlag
      ? displayTimeSecondFormat(destinationData?.ata)
      : displayTimeSecondFormat(destinationData?.eta)
    const destinationDataStatus = standardizeText(destinationData.status)
    let colorText = statusColor[destinationDataStatus]
    if (!colorText) {
      colorText = '#000000'
    }
    const borderColorStyle =
      nextStopIsDestination &&
      !completedFlag &&
      destinationData.status &&
      statusColor[destinationDataStatus]
        ? statusColor[destinationDataStatus]
        : '#ffffff'
    return (
      <EndStage alignItemsFlexStart>
        <Span
          variant="ellipsis"
          style={{
            width: '100%',
            position: 'relative',
            top: '-2px',
            fontSize: '12px',
            boxSizing: 'content-box'
          }}
        >
          {destination || '-'}
        </Span>
        <IconWithPopover
          destinationDetails={originalData}
          transportMode={transportMode}
          popoverType="destination"
          icon={
            <FaFlag
              style={{
                ...style.iconStyle,
                height: '28px',
                width: '28px',
                color: '#fff',
                boxSizing: 'border-box',
                padding: '0 6px',
                marginTop: isEmpty(stops) ? '8px' : '6px',
                borderColor: borderColorStyle,
                background: colorCode,
                position: 'relative',
                top: isEmpty(stops) ? '-1px' : '-3px',
                left: '-10px'
              }}
            />
          }
        />
        <Flex
          column
          alignItemsCenter
          style={{
            gap: '0.25em',
            marginTop: isEmpty(stops) ? '-4px' : '-10px',
            width: '100%',
            boxSizing: 'content-box'
          }}
        >
          <FieldValue
            style={{
              marginTop: 4,
              width: '160px',
              justifyContent: 'flex-start',
              boxSizing: 'content-box'
            }}
            label={sta != '-' ? 'STA:' : '\u00a0'}
            value={sta != '-' ? sta : '\u00a0'}
            valueStyle={{ fontSize: '12px' }}
            labelStyle={{ fontSize: '12px' }}
          />
          <FieldValue
            style={{
              width: '160px',
              justifyContent: 'flex-start',
              boxSizing: 'content-box'
            }}
            labelStyle={{
              color: aetaFlag ? '#000' : colorText,
              fontSize: '12px'
            }}
            valueStyle={{
              color: aetaFlag ? '#000' : colorText,
              fontSize: '12px'
            }}
            label={aeta != '-' ? (aetaFlag ? 'ATA:' : 'ETA:') : '\u00a0'}
            value={aeta != '-' ? aeta : '\u00a0'}
          />
        </Flex>
      </EndStage>
    )
  }

  function InitialStage() {
    const completedStopFlag = stops.find(
      (item: any) => item.state === 'completed' || item?.ata?.time
    )
    const intialStopFlag = !isEmpty(stops)
      ? stops[0]?.ata?.time && !stops[0]?.atd?.time
      : false
    const showSolid =
      completedStopFlag ||
      completedFlag ||
      intialStopFlag ||
      nextStopIsDestination
    const atOriginFlag =
      !nextStopIsOrigin && !completedOriginFlag && !furtherStopsCompletedFlag
    const completedDestinationFlag = completedFlag || destinationData?.ata?.time
    const currentNodeFlag =
      completedOriginFlag &&
      !furtherStopsCompletedFlag &&
      !completedDestinationFlag
    const showBlue =
      completedOriginFlag ||
      furtherStopsCompletedFlag ||
      completedDestinationFlag

    const lineStyle = currentNodeFlag ? 'dotted' : 'solid'
    return (
      <Stage
        style={{
          stage: {
            width: `${timelineMinWidthPerStop}%`
          }
        }}
        line={{
          color: showBlue ? '#1192E8' : 'gray5',
          style: lineStyle,
          width: null
        }}
        showVehicle={showSolid || atOriginFlag ? false : whenToShowVehicle()}
        transportationMode={noLineToShow ? null : transportMode}
        rowData={rowData}
        locationNotFound={locationNotFound}
      />
    )
  }

  function Stoppages(): any {
    if (isEmpty(stops)) {
      const setNullWidth =
        untrackedFlag || nextStopIsOrigin || completedFlag || scheduledFlag
      const showSolid = completedFlag || destinationData?.ata?.time
      const atOriginFlag =
        !nextStopIsOrigin && !completedOriginFlag && !furtherStopsCompletedFlag
      const completedDestinationFlag =
        completedFlag || destinationData?.ata?.time
      const currentNodeFlag =
        completedOriginFlag &&
        !furtherStopsCompletedFlag &&
        !completedDestinationFlag
      const showBlue =
        completedOriginFlag ||
        furtherStopsCompletedFlag ||
        completedDestinationFlag
      const lineStyle = currentNodeFlag ? 'dotted' : 'solid'
      return (
        <Stage
          details={stop}
          line={{
            color: showBlue ? '#1192E8' : 'gray5',
            style: lineStyle,
            width: setNullWidth ? null : percCompleted || null
          }}
          noStops={showSolid || atOriginFlag ? false : true}
          showVehicle={false}
          transportationMode={noLineToShow ? null : transportMode}
          rowData={rowData}
          locationNotFound={locationNotFound}
        />
      )
    } else {
      return stops.map((stop: any, i: number, data: any) => {
        const stopsLength = stops.length
        const aetaFlag = destinationData?.ata?.time || completedFlag
        var skippedFlag = aetaFlag
        const ataFlag = stop?.ata?.time
        const atdFlag = stop?.atd?.time
        for (var stopIndex = i + 1; stopIndex < stopsLength; stopIndex++) {
          if (
            stops[stopIndex].ata.time ||
            stops[stopIndex].atd.time ||
            stops[stopIndex].state == 'completed'
          ) {
            skippedFlag = true
            break
          }
        }
        const atStopFlag = ataFlag && !atdFlag

        const hideETA =
          i <= nextStopIndex - 1 || nextStopIsDestination || completedFlag
        const showVehicle =
          completedFlag || (nextStopIsDestination && nextStop?.ata?.time)

        const nextStopId = nextStop?.nodeId
        const nextStopSequence = nextStop?.sequence
        let nextStopExtraInfo: { text: string | null; fill: string } = {
          text: null,
          fill: 'black'
        }
        const isNextStopIndex = i == nextStopIndex
        if (
          rowData.inState !== 'completed' &&
          !rowData.isActive &&
          (stop.nodeId === nextStopId, stop.sequence === nextStopSequence)
        ) {
          const appointmentTime = stop.appointmentWindowInMins
            ? stop.appointmentWindowInMins * 60 * 1000
            : 0
          const etaTime = new Date(stop.eta.time).getTime()
          const staTime = new Date(stop.sta.time).getTime()
          if (etaTime >= staTime + appointmentTime) {
            nextStopExtraInfo = { text: 'Delayed', fill: statusColor.delayed }
          } else if (etaTime < staTime) {
            nextStopExtraInfo = { text: 'Early', fill: statusColor.early }
          } else {
            nextStopExtraInfo = { text: 'Ontime', fill: statusColor.on_time }
          }
        }
        const atNodeFlag =
          !(stop.state == 'completed') && atStopFlag && !skippedFlag
        const showVehicleCondition =
          i === nextStopIndex - 1 ||
          (nextStopIsDestination && i === stopsLength - 1)
            ? whenToShowVehicle()
            : false
        const ShowVehicleAtNode = showVehicle ? false : showVehicleCondition
        const stopCompletedFlag =
          stop.state === 'completed' || atdFlag || (ataFlag && skippedFlag)
        const colorFlag = stop.nodeColor !== 'grey'
        const colorCode =
          stop.nodeColor === 'green'
            ? '#279B4E'
            : stop.nodeColor === 'blue'
            ? '#1192E8'
            : stop.nodeColor === 'orange'
            ? '#EE6252'
            : '#BFBFBF'
        const nodeSkippedFlag = skippedFlag && !ataFlag && !atdFlag
        const nodeIconStyle = {
          border: '4px solid',
          height: '12px',
          width: '12px',
          position: 'relative',
          top: '-3px',
          boxSizing: 'content-box'
        } as React.CSSProperties
        const stopStatus = standardizeText(stop.status)
        const statusColorForIcon = statusColor[stopStatus] ?? '#BFBFBF'
        const nodeIconToDisplay = colorFlag ? (
          <FaCheckCircle
            color={colorCode}
            size={style.iconSize}
            style={{ background: '#fff', position: 'relative', top: '-3px' }}
          />
        ) : isNextStopIndex && !skippedFlag ? (
          <FaDotCircle
            color="#00539A"
            size={style.iconSize}
            style={{
              background: '#fff',
              fill: statusColorForIcon,
              position: 'relative',
              top: '-3px'
            }}
          />
        ) : atNodeFlag ? (
          <AtNodeMode
            showVehicle={ShowVehicleAtNode}
            modeType={transportMode}
            atOrigin={false}
            stops={stops}
          />
        ) : nodeSkippedFlag ? (
          <div
            style={{
              ...style.iconStyle,
              ...nodeIconStyle,
              borderColor: '#ADADAD',
              background: '#fff',
              backgroundColor: '#fff'
            }}
          />
        ) : (
          <div
            style={{
              ...style.iconStyle,
              ...nodeIconStyle,
              borderColor: '#ADADAD',
              background: '#ADADAD',
              backgroundColor: '#ADADAD'
            }}
          />
        )
        const currentNodeFlag =
          (nextStopIndex == i + 1 ||
            (nextStop?.nodeId == destinationData?.nodeId &&
              i == stops.length - 1)) &&
          !atNodeFlag &&
          !skippedFlag
        const showBlue = skippedFlag || currentNodeFlag
        const lineStyle = currentNodeFlag ? 'dotted' : 'solid'
        return (
          <Stage
            name={stop.name || stop.city}
            key={`${stop.nodeId}_${stop.sequence}`}
            icon={nodeIconToDisplay}
            details={stop}
            style={{
              stage: { width: `${timelineMinWidthPerStop}%` }
            }}
            hideETA={hideETA}
            line={{
              color: showBlue ? '#1192E8' : 'gray5',
              style: lineStyle,
              width: null
            }}
            showVehicle={
              showVehicle || atNodeFlag ? false : showVehicleCondition
            }
            tripRunningStatus={
              stop.status?.toLowerCase().includes('untrack')
                ? {
                    label: 'Untracked Status',
                    labelStatus: '\u00a0',
                    style: { color: colorOption['Untracked State'] }
                  }
                : getRunningStatusObj(stop.arrivalTimediffMs)
            }
            transportationMode={noLineToShow ? null : transportMode}
            rowData={rowData}
            locationNotFound={locationNotFound}
          />
        )
      })
    }
  }

  return (
    <TimeLine id={originalData.slug} ref={ref} padding="2px 5px">
      {loading ? (
        <div style={{ padding: '45px 0  45px 45vw' }}>
          <Loader height="20px" width="20px" variant="spin" />
        </div>
      ) : (
        <>
          <Origin />
          <Flex style={{ flex: 1, overflow: 'auto' }}>
            {!isEmpty(stops) ? <InitialStage /> : null}
            <Stoppages />
          </Flex>
          <Destination />
        </>
      )}
    </TimeLine>
  )
}
